import {
  coordEq,
  coordKey,
  wouldLeavingTileResetCapture,
  type Coord,
} from "@dailywars/core/engine";
import type { GameState } from "@dailywars/core/engine";

import { buildPreviewAnimation } from "#/game/animation/preview";
import type { CanvasMoveAnimation } from "#/game/animation/types";
import type { CaptureResetResume, SelectionState } from "#/game/selection/types";
import type { StoreState } from "#/game/store/types";

type MovePreviewSelection = Extract<SelectionState, { mode: "move-preview" }>;

export function hiddenCaptureBarTileKeysFromSelection(
  selection: SelectionState,
  state: GameState,
): string[] {
  if (selection.mode !== "move-preview") return [];
  const unit = state.units.find((candidate) => candidate.id === selection.unitId);
  if (!unit) return [];
  if (
    !wouldLeavingTileResetCapture({
      state,
      unit,
      destination: selection.destination,
    })
  ) {
    return [];
  }
  return [coordKey(unit.pos)];
}

export function needsCaptureResetConfirmation(
  state: StoreState,
  { unitId, destination }: { unitId: string; destination: Coord },
): boolean {
  if (!state.captureResetWarning) return false;
  const unit = state.localState.units.find((candidate) => candidate.id === unitId);
  if (!unit) return false;
  return wouldLeavingTileResetCapture({
    state: state.localState,
    unit,
    destination,
  });
}

export function enterCaptureResetConfirmation({
  state,
  unitId,
  priorSelection,
  preview,
  resume,
}: {
  state: StoreState;
  unitId: string;
  priorSelection: SelectionState;
  preview: MovePreviewSelection;
  resume: CaptureResetResume;
}): StoreState {
  return {
    ...state,
    selection: {
      mode: "confirm-capture-reset",
      unitId,
      priorSelection,
      preview,
      resume,
    },
  };
}

export function cancelCaptureResetConfirmation(state: StoreState): StoreState {
  const sel = state.selection;
  if (sel.mode !== "confirm-capture-reset") return state;
  return {
    ...state,
    selection: sel.priorSelection,
    previewAnimation: null,
  };
}

export function tryEnterMovePreview({
  state,
  priorSelection,
  movePreview,
  previewAnimation,
}: {
  state: StoreState;
  priorSelection: SelectionState;
  movePreview: MovePreviewSelection;
  previewAnimation: CanvasMoveAnimation;
}): StoreState {
  const unit = state.localState.units.find((candidate) => candidate.id === movePreview.unitId);
  if (!unit) return { ...state, selection: { mode: "none" }, previewAnimation: null };

  const next = { ...state, selection: movePreview, previewAnimation };

  if (coordEq(movePreview.destination, unit.pos)) {
    return next;
  }

  if (
    !needsCaptureResetConfirmation(state, {
      unitId: movePreview.unitId,
      destination: movePreview.destination,
    })
  ) {
    return next;
  }

  return enterCaptureResetConfirmation({
    state: { ...state, previewAnimation: null },
    unitId: movePreview.unitId,
    priorSelection,
    preview: movePreview,
    resume: { kind: "move-preview" },
  });
}

export function executeCaptureResetResume(state: StoreState): StoreState {
  const sel = state.selection;
  if (sel.mode !== "confirm-capture-reset") return state;

  if (sel.resume.kind === "move-preview") {
    const unit = state.localState.units.find((candidate) => candidate.id === sel.preview.unitId);
    const previewAnimation =
      unit === undefined
        ? null
        : buildPreviewAnimation({
            unitId: sel.preview.unitId,
            from: unit.pos,
            path: sel.preview.path,
            fastPlayback: state.fastPlayback,
          });
    return { ...state, selection: sel.preview, previewAnimation };
  }

  return state;
}
